/**
 * Path 1: Memory Layer — Session intelligence that compounds.
 *
 * 4 scenes showing how Cortex eliminates session amnesia:
 * briefing, anti-pattern saves, cross-project transfer, learning proof.
 */

import type { AdventurePath } from './paths';

export const MEMORY_LAYER_PATH: AdventurePath = {
  id: 'memory',
  title: 'Memory Layer',
  subtitle: 'Intelligence that survives session boundaries',
  scenes: [
    // ── Scene 1: Monday Morning ──────────────────────
    {
      id: 'memory-1',
      title: 'Monday Morning',
      lines: [
        { text: '$ cortex briefing', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Daily Intelligence Briefing ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Portfolio Health (6 projects):', style: 'narration' },
        { text: '', style: 'blank' },
        { text: '  Vortex Backend    2,835 tests   HEALTHY', style: 'success' },
        { text: '  Vortex Frontend     113 tests   HEALTHY', style: 'success' },
        { text: '  Cortex              853 tests   HEALTHY', style: 'success' },
        { text: '  Alpha Arena         603 tests   HEALTHY', style: 'success' },
        { text: '  Pupil             1,255 tests   HEALTHY', style: 'success' },
        { text: '  DJ-CoPilot           41 tests   STALE (9 days)', style: 'warning' },
        { text: '', style: 'blank' },
        { text: '  Total: 5,700+ tests | 0 regressions overnight', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Anomalies:', style: 'header' },
        { text: '  > HRRR EMOS auto-fit completed (38K pairs)', style: 'data' },
        { text: '  > GFS Wave now in scheduler', style: 'data' },
        { text: '  > Alpha Arena cold start: 3 trades, +$127', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Goal velocity: +23% vs last week', style: 'success' },
        { text: '  Stale goal: DJ-CoPilot harmonic mixing (9 days)', style: 'warning' },
        { text: '', style: 'blank' },
        { text: '  -> Resume Vortex competition validation', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '10 seconds. Full portfolio context.', style: 'narration' },
        { text: 'No "where was I?" No re-reading old notes.', style: 'narration' },
        { text: 'Monday morning with perfect recall.', style: 'dim' },
      ],
      choices: [
        { label: 'See how memory prevents repeated mistakes', next: 'memory-2', tag: 'ANTI-PATTERNS' },
        { label: 'Skip to the learning proof', next: 'memory-4', tag: 'METRICS' },
      ],
    },

    // ── Scene 2: The Anti-Pattern Save ───────────────
    {
      id: 'memory-2',
      title: 'The Anti-Pattern Save',
      lines: [
        { text: '$ cortex intelligence "wind data returning NaN after refactor"', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '  Memory match found (confidence: 0.89)', style: 'warning' },
        { text: '', style: 'blank' },
        { text: '  "GRIB interp() needs 0-360 lon convention"', style: 'data' },
        { text: '  Stored: Session #412, Feb 21 (Vortex)', style: 'dim' },
        { text: '  Accessed: 4 times previously', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  Context: ds.interp() does NOT auto-convert', style: 'narration' },
        { text: '  negative lon on 0-360 grids. ds.sel() does.', style: 'narration' },
        { text: '  Always use lon_use from _get_grid_indices().', style: 'narration' },
        { text: '', style: 'blank' },
        { text: '  -> This bug has been solved. Apply known fix.', style: 'success' },
        { text: '', style: 'blank' },
        { text: 'Without Cortex: 45 minutes debugging a known issue.', style: 'dim' },
        { text: 'With Cortex: 3 seconds to the exact fix.', style: 'accent' },
        { text: '', style: 'blank' },
        { text: 'This is the difference between session amnesia', style: 'narration' },
        { text: 'and compounding intelligence. Every bug you fix', style: 'narration' },
        { text: 'makes the NEXT session faster.', style: 'narration' },
      ],
      hubEvent: {
        task: 'Memory retrieval: GRIB longitude convention',
        complexity: 'simple',
        selectedProvider: 'Groq',
        selectedModel: 'Llama 8B',
        cost: 0.003,
        baselineCost: 0.42,
        reason: 'Semantic search + embedding lookup. No reasoning needed.',
      },
      choices: [
        { label: 'See cross-project pattern transfer', next: 'memory-3', tag: 'PATTERNS' },
        { label: 'Jump to learning metrics', next: 'memory-4', tag: 'PROOF' },
      ],
    },

    // ── Scene 3: Cross-Project Transfer ──────────────
    {
      id: 'memory-3',
      title: 'Cross-Project Transfer',
      lines: [
        { text: '$ cortex intelligence "implement rate limiting for new API"', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Pattern Matches (3 found) ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  1. Alpha Arena: Token bucket, 500 req/min', style: 'data' },
        { text: '     Production 3 months, 0 incidents', style: 'dim' },
        { text: '     Confidence: 0.91', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  2. Vortex API: SlowAPI middleware, 100/min per IP', style: 'data' },
        { text: '     3 months uptime, battle-tested', style: 'dim' },
        { text: '     Confidence: 0.88', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  3. Anti-pattern: Never rate-limit health endpoints', style: 'warning' },
        { text: '     Source: PR #312 postmortem', style: 'dim' },
        { text: '     (Killed monitoring for 3 hours)', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  -> Recommend: SlowAPI from Vortex (proven, FastAPI native)', style: 'accent' },
        { text: '', style: 'blank' },
        { text: 'Six projects. Hundreds of sessions.', style: 'narration' },
        { text: 'Every pattern, every postmortem, every gotcha --', style: 'narration' },
        { text: 'available in every future session, forever.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'This is portfolio intelligence.', style: 'accent' },
      ],
      choices: [
        { label: 'See the learning proof', next: 'memory-4', tag: 'CALIBRATION' },
        { label: 'Back to paths', next: 'hub', tag: 'PATHS' },
      ],
    },

    // ── Scene 4: The Learning Proof ──────────────────
    {
      id: 'memory-4',
      title: 'The Learning Proof',
      lines: [
        { text: '$ cortex calibration', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Confidence Calibration (90 days) ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Predicted 90%+    Actual 87.3%  ~overconfident', style: 'warning' },
        { text: '  Predicted 70-80%  Actual 74.1%  calibrated', style: 'success' },
        { text: '  Predicted 50-60%  Actual 52.8%  calibrated', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Recommendation accuracy: 73% (30-day window)', style: 'data' },
        { text: '  Best:  Bug fixes (89%)', style: 'success' },
        { text: '  Worst: Architecture decisions (51%)', style: 'warning' },
        { text: '  Trend: +8% improvement since January', style: 'success' },
        { text: '', style: 'blank' },
        { text: '$ cortex metrics --learning', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '  Knowledge base:   2,847 entries', style: 'data' },
        { text: '  Outcomes tracked:   853', style: 'data' },
        { text: '  Insights derived:   369', style: 'data' },
        { text: '  Feedback loops:      47 active', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Session time saved: ~18 min/day', style: 'success' },
        { text: '  Bugs prevented:     ~3.2/week', style: 'success' },
        { text: '', style: 'blank' },
        { text: 'When Cortex says 80% confident -- it IS right', style: 'narration' },
        { text: '~80% of the time. Trust is earned, not asserted.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'Honesty compounds. Every outcome trains the', style: 'accent' },
        { text: 'next recommendation. The system gets better', style: 'accent' },
        { text: 'because it measures itself.', style: 'accent' },
      ],
      choices: [
        { label: 'Explore another path', next: 'hub', tag: 'PATHS' },
        { label: 'See the cost engine', next: 'cost-1', tag: 'COST' },
      ],
    },
  ],
};
